/** JWK <-> did:key (P-256 multicodec 0x1200) and did:beacon derivation (F4, ADR-12). */
import type { webcrypto } from "node:crypto";
import {
  b64urlDecode,
  b64urlEncode,
  base32lower,
  base58btcDecode,
  base58btcEncode,
  beBytes32,
  beInt,
  concatBytes,
  utf8,
} from "./bytes.js";

export type Jwk = webcrypto.JsonWebKey;

const P256_PREFIX = new Uint8Array([0x80, 0x24]); // varint(0x1200)

const b64u = { enc: b64urlEncode, dec: b64urlDecode };

/** `"did:key:z" + base58btc( varint(0x1200) || compressedPoint33 )` */
export function didKeyFromJwk(jwk: { x: string; y: string }): string {
  const x = b64u.dec(jwk.x);
  const y = b64u.dec(jwk.y);
  if (x.length !== 32 || y.length !== 32) throw new Error("did:key: JWK x/y must be 32 bytes");
  const compressed = concatBytes(new Uint8Array([y[31] & 1 ? 3 : 2]), x);
  return "did:key:z" + base58btcEncode(concatBytes(P256_PREFIX, compressed));
}

/** Strip `did:key:z`, base58-decode, validate multicodec + shape; return the 33-byte compressed point. */
export function compressedPointFromDidKey(didKey: string): Uint8Array {
  if (!didKey.startsWith("did:key:z")) throw new Error("did:key: missing did:key:z prefix");
  const raw = base58btcDecode(didKey.slice("did:key:z".length));
  if (raw.length !== 35) throw new Error(`did:key: expected 35 bytes, got ${raw.length}`);
  if (raw[0] !== P256_PREFIX[0] || raw[1] !== P256_PREFIX[1]) {
    throw new Error("did:key: not a P-256 multicodec (0x1200)");
  }
  const point = raw.slice(2);
  if (point[0] !== 0x02 && point[0] !== 0x03) {
    throw new Error("did:key: compressed point must start 02/03");
  }
  return point;
}

// P-256 curve parameters for point decompression: y² = x³ − 3x + b (mod p)
const P = BigInt("0xFFFFFFFF00000001000000000000000000000000FFFFFFFFFFFFFFFFFFFFFFFF");
const B = BigInt("0x5AC635D8AA3A93E7B3EBBD55769886BC651D06B0CC53B0F63BCE3C3E27D2604B");

function mod(a: bigint, m: bigint): bigint {
  const r = a % m;
  return r < 0n ? r + m : r;
}

function modPow(base: bigint, exp: bigint, m: bigint): bigint {
  let result = 1n;
  let b = mod(base, m);
  let e = exp;
  while (e > 0n) {
    if (e & 1n) result = (result * b) % m;
    b = (b * b) % m;
    e >>= 1n;
  }
  return result;
}

/** Decompress a did:key's point and return the public JWK (throws if x is off-curve). */
export function jwkFromDidKey(didKey: string): Jwk & { x: string; y: string } {
  const point = compressedPointFromDidKey(didKey);
  const x = beInt(point.slice(1));
  if (x >= P) throw new Error("did:key: x out of field range");
  const rhs = mod(x * x * x - 3n * x + B, P);
  // p ≡ 3 mod 4 → sqrt = c^((p+1)/4) mod p
  let y = modPow(rhs, (P + 1n) >> 2n, P);
  if ((y * y) % P !== rhs) throw new Error("did:key: point is not on the P-256 curve");
  const wantOdd = point[0] === 0x03;
  const yIsOdd = (y & 1n) === 1n;
  if (yIsOdd !== wantOdd) y = P - y;
  return {
    kty: "EC",
    crv: "P-256",
    x: b64u.enc(point.slice(1)),
    y: b64u.enc(beBytes32(y)),
  };
}

/** `"did:beacon:" + base32lower( sha256( utf8(genesis did:key string) )[0..16] )` — 26 chars. */
export async function deriveDidBeacon(genesisDidKey: string): Promise<string> {
  const h = new Uint8Array(await crypto.subtle.digest("SHA-256", utf8(genesisDidKey)));
  return "did:beacon:" + base32lower(h.slice(0, 16));
}
